// 時間前入札の「売買逆転 (価格交差)」検知 (AC-BIN-045 / AC-BIN-046)。
// 入札追加タブ (`pages/IntradayBidding`) と CSV 取込の Step2 (`pages/IntradayCsvImport`) が使う。
//
// spec `docs/specs/bidding/intraday-bidding.md` AC-BIN-045:
//   同一受渡日・同一エリア・同一コマで「買 価格 ≧ 売 価格」の価格交差を検知する
//   (検知単位 (DeliveryDate, Slot, Area)、同一組織内の入札同士のみが対象)。
//   いずれか一方が成行の場合は、価格に関わらず対向札が 1 枚でもあれば交差とみなす。
//   検知対象プールは、これから提出する札と、板にある / 板に載る予定の札
//   (予約中〈AC-BIN-089〉・承認申請中・承認済・送信中〈送れるまで送り直している札を含む〉・
//   入札中〈一部約定しているものを含む〉) とし、提出しない下書き・取消・失効・全約定・未約定・
//   差戻しは含めない (issue #8529 / 2026-09-23 PdM 判断、 #10680。 `isInIntradayInversionPool`)。
// AC-BIN-046: 検知結果をアラートバナーに「検知 N 件」として表示する。
//   検知件数 > 0 でも提出はブロックしない (spot AC-BSP-009 と同じ扱い。
//   意図的な両建てがありうるため)。
//
// spot 側の同型ロジック `SpotBidding/models/spotBidValidation.ts` `detectInversions`
// を参考にした純粋関数。 検知単位 (DeliveryDate, Slot, Area) のうち受渡日は
// 呼び出し元が 1 つの受渡日の札に絞って担保する (入札追加タブは表示中の受渡日、
// CSV 取込は行ごとの受渡日で分ける)。 `detectIntradayBidInversions` 自体は受渡日を
// 扱わない。 = 複数受渡日の札を渡してはならない (渡すと別受渡日の 買/売 が交差扱いになる)。
//
// 成行の規則 (AC-BSP-009 / AC-BIN-045: 成行 = 対向札があれば価格に関わらず交差) は spot も
// 同じで、 spot 側は `SpotBidding/models/spotBidValidation.ts` の `crossesSpotPrice` が持つ
// (共通化は #8149)。 本関数の成行の判定は `isIntradayMarketOrder` (`orderMethod === MARKET`)
// を通す。 成行の札は価格 "0" を運ぶが指値 0 円ではない。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalState,
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingIntradayOrderOrderMethod } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'

import { isKnownOnBoardByBidNo } from '~/models/intradayJepxBidNo'
import { isIntradayMarketOrder } from '~/models/intradayOrderMethod'

/** 逆転判定に必要な最小の形。 入札追加タブの表示行 (`IntradayBidRow`) はこれを満たす。 */
export type IntradayInversionInput = {
  id: string
  areaCode: AreaCode
  /** コマ番号 1〜48。 */
  timeCode: number
  direction: Side
  /** 入札価格 円/kWh (Decimal string)。 成行は "0" を運ぶ (判定は orderMethod を見る)。 */
  priceYenPerKwh: string
  orderMethod?: BiddingIntradayOrderOrderMethod
}

export type IntradayInversion = {
  /** 逆転に巻き込まれている札 id 群 (= 同 area × 同 koma の buy + sell 全件)。 */
  bidIds: string[]
  areaCode: AreaCode
  timeCode: number
}

/**
 * 同一エリア・同一コマ (呼び出し元が同一受渡日に絞った上で) の入札群から
 * 売買逆転 (買 価格 ≧ 売 価格) を検知する。
 *
 * 判定規則 (AC-BIN-045):
 * - 買値 ≧ 売値 は境界の一致 (等号) も交差に含める。
 * - いずれか一方に成行札が 1 枚でもあれば、 価格に関わらず対向札が 1 枚でもあることで交差とみなす。
 * - 検知対象プールの絞り込み (ステータス・受渡日) は呼び出し元が行う (`isInIntradayInversionPool`)。
 */
// 業務判定を frontend に置く例外: 提出を止めない警告 (AC-BIN-046) のため画面で判定する
export const detectIntradayBidInversions = (
  bids: readonly IntradayInversionInput[],
): IntradayInversion[] => {
  const groups = new Map<string, IntradayInversionInput[]>()
  for (const b of bids) {
    const key = `${b.areaCode}-${b.timeCode}`
    const arr = groups.get(key) ?? []
    arr.push(b)
    groups.set(key, arr)
  }

  const out: IntradayInversion[] = []
  for (const arr of groups.values()) {
    const buys = arr.filter((b) => b.direction === Side.BUY)
    const sells = arr.filter((b) => b.direction === Side.SELL)
    if (buys.length === 0 || sells.length === 0) continue

    const hasMarketOrderSide =
      buys.some(isIntradayMarketOrder) || sells.some(isIntradayMarketOrder)

    // 成行が一方でも絡めば、 対向札が (指値・成行問わず) 1 枚でもあることで交差
    // (価格を見ない)。 それ以外は指値同士の価格交差判定。
    const crossed = hasMarketOrderSide
      ? true
      : Math.max(...buys.map((b) => Number(b.priceYenPerKwh))) >=
        Math.min(...sells.map((b) => Number(b.priceYenPerKwh)))

    if (crossed) {
      out.push({
        bidIds: arr.map((b) => b.id),
        areaCode: arr[0].areaCode,
        timeCode: arr[0].timeCode,
      })
    }
  }
  return out
}

/**
 * プールに入れる業務ステータス。 予約中は送信軸 (RESERVED) で表すので業務ステータスはこのいずれかになる。
 * 部分約定 (PARTIAL_MATCHED) は GC 前なら残量が板にある (板取消すると取消 CANCELLED に遷移する)。
 * 送信失敗 (FAILED) は送れるまで送り直す札で、 画面では送信中 (#10680、 AC-BIN-035)。 GC まで
 * 送り直すので板に載る予定の札に入れる (送信が終わった札は下の送信軸の判定で外す)。
 */
const POOL_BID_STATUSES: ReadonlySet<BiddingIntradayOrderStatus> = new Set([
  BiddingIntradayOrderStatus.DRAFT,
  BiddingIntradayOrderStatus.PENDING,
  BiddingIntradayOrderStatus.APPROVED,
  BiddingIntradayOrderStatus.PLACED,
  BiddingIntradayOrderStatus.PARTIAL_MATCHED,
  BiddingIntradayOrderStatus.FAILED,
])

/**
 * 送信軸の送信失敗 (確定)。 送り直さないので板に届かない札として外す (再送待ち SEND_FAILED は
 * 送り直すので外さない)。 ただし入札番号で板にあると分かっている札 (`isKnownOnBoardByBidNo`) は
 * 外さない。 backend の `IntradayOrder.IsOnOrBoundForBoard` と同じ規則。
 */
const SEND_ENDED_STATUSES: ReadonlySet<BiddingBidSendStatus> = new Set([
  BiddingBidSendStatus.SEND_FAILED_FINAL,
])

/**
 * 検知対象プールの判定に使う、 一覧 RPC の注文 1 件の最小の形。
 * 承認軸は一覧 RPC の値をそのまま渡す (一覧の表示用に写した値は、 下書きと承認済を
 * 区別しない)。
 */
export type IntradayInversionPoolOrder = {
  bidStatus: BiddingIntradayOrderStatus
  approvalState: BiddingApprovalState
  sendStatus: BiddingBidSendStatus
  /** JEPX 入札番号。 backend が値を持つ札だけ設定される。 */
  jepxBidNo?: string
  /** コマ番号 1〜48。 */
  timeCode: number
}

/**
 * まだ提出も承認申請もしていない下書きか。 承認申請中・承認済の札も、 提出するまで
 * 業務ステータスは下書きのまま (承認軸だけが進む) なので、 承認軸も見る。
 */
const isUnsubmittedDraft = (
  order: Omit<IntradayInversionPoolOrder, 'timeCode'>,
): boolean =>
  order.bidStatus === BiddingIntradayOrderStatus.DRAFT &&
  order.approvalState !== BiddingApprovalState.PENDING &&
  order.approvalState !== BiddingApprovalState.APPROVED

/**
 * 注文 1 件の状態 (業務ステータス・送信軸・承認軸) が売買逆転の検知対象プールに当たるか
 * (AC-BIN-045、 issue #8529)。 GC を過ぎた札を外す判定は含まない
 * (`isInIntradayInversionPool` か呼び出し元が行う)。
 *
 * 入れる: 板にある / 板に載る予定の札 (予約中・承認申請中・承認済・送信中〈送れるまで
 *   送り直している札を含む、 #10680〉・入札中〈一部約定しているものを含む〉)。 未提出の下書きは
 *   `includeUnsubmittedDrafts` で決める (AC-BIN-045 の対象は「これから提出する札」で、
 *   提出しない下書きは含めない)。
 * 入れない: 取消・全約定・送信が終わった札 (送信軸 SEND_FAILED_FINAL)・差戻し。
 *   ただし入札番号で板にあると分かっている札 (`isKnownOnBoardByBidNo`) は、 送信軸が失敗系の
 *   ままでも入札中と同じに入れる (AC-BIN-115、 #10491)。
 *
 * @param options.includeUnsubmittedDrafts 未提出の下書きを含めるか。 入札追加タブは、
 *   一覧に表示中の未送信の札を提出するので、 その札を含める (札ごとの判定は
 *   `detectIntradayDeliveryDateInversions`)。 CSV 取込と承認確認は、 提出するのが取り込む行・
 *   承認する札だけなので含めない。
 */
export const isIntradayInversionPoolState = (
  order: Omit<IntradayInversionPoolOrder, 'timeCode'>,
  { includeUnsubmittedDrafts }: { includeUnsubmittedDrafts: boolean },
): boolean =>
  POOL_BID_STATUSES.has(order.bidStatus) &&
  (!SEND_ENDED_STATUSES.has(order.sendStatus) ||
    isKnownOnBoardByBidNo(order)) &&
  order.approvalState !== BiddingApprovalState.REJECTED &&
  (includeUnsubmittedDrafts || !isUnsubmittedDraft(order))

/**
 * 注文 1 件を売買逆転の検知対象プールに入れるか (AC-BIN-045、 issue #8529)。 状態の判定は
 * `isIntradayInversionPoolState` と同じで、 加えて GC を過ぎたコマの札を外す。
 * 失効・未約定・GC 到達で終わった部分約定は一覧 RPC のステータス値に独立した区分が無く
 * (内部状態は承認申請中 / 入札中 / 部分約定のまま)、 spec の遷移条件はいずれも GC 到達
 * (§5) なので、 GC 境界コマ (`gcBoundarySlot`) より前のコマの札を外すことで除く。
 *
 * @param gcBoundary 注文の受渡日の GC 境界コマ (このコマより前は GC 通過済み)。
 */
export const isInIntradayInversionPool = (
  order: IntradayInversionPoolOrder,
  gcBoundary: number,
  options: { includeUnsubmittedDrafts: boolean },
): boolean =>
  order.timeCode >= gcBoundary && isIntradayInversionPoolState(order, options)
